import { API } from '@/lib/api';
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Truck, Package, Car, Home, Wrench, Users, Baby, Sparkles, ChefHat,
  Snowflake, Trees, MapPin, Clock, User, Navigation, MessageCircle,
  Loader2, ArrowLeft, CheckCircle2, XCircle, Activity, ChevronRight,
  DollarSign, X
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import axios from "axios";



const SVC = {
  cargo_delivery: { label: "Cargo Delivery", icon: Truck, color: "bg-lime-500" },
  courier: { label: "Courier", icon: Package, color: "bg-sky-500" },
  towing: { label: "Towing", icon: Car, color: "bg-orange-500" },
  movers: { label: "Movers", icon: Home, color: "bg-purple-500" },
  roadside_assistance: { label: "Roadside", icon: Wrench, color: "bg-red-500" },
  laborer: { label: "Laborer", icon: Users, color: "bg-rose-500" },
  nanny: { label: "Nanny", icon: Baby, color: "bg-pink-500" },
  cleaning: { label: "Cleaning", icon: Sparkles, color: "bg-cyan-500" },
  private_chef: { label: "Private Chef", icon: ChefHat, color: "bg-amber-500" },
  snow_plowing: { label: "Snow Plowing", icon: Snowflake, color: "bg-blue-500" },
  lawn_mowing: { label: "Lawn Mowing", icon: Trees, color: "bg-green-500" },
};

const TRIP_LABELS = {
  en_route: "En Route",
  arriving: "Arriving",
  arrived: "Arrived",
  completed: "Completed",
};

const StatusDot = ({ status, trip }) => {
  if (status === "completed") return <span className="w-2 h-2 rounded-full bg-emerald-500" />;
  if (status === "cancelled" || status === "declined") return <span className="w-2 h-2 rounded-full bg-red-400" />;
  if (status === "accepted") return <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />;
  return <span className="w-2 h-2 rounded-full bg-stone-300" />;
};

const StatusText = ({ status, trip }) => {
  if (status === "accepted" && trip) return <span className="text-blue-600 font-semibold text-xs">{TRIP_LABELS[trip] || trip}</span>;
  if (status === "completed") return <span className="text-emerald-600 font-semibold text-xs">Completed</span>;
  if (status === "cancelled") return <span className="text-red-500 font-semibold text-xs">Cancelled</span>;
  if (status === "declined") return <span className="text-stone-400 font-semibold text-xs">Declined</span>;
  return <span className="text-stone-400 font-semibold text-xs capitalize">{status}</span>;
};

const fmtDate = (d) => {
  if (!d) return "";
  try {
    const dt = new Date(d);
    return dt.toLocaleDateString("en-CA", { month: "short", day: "numeric" });
  } catch { return ""; }
};

const fmtTime = (d) => {
  if (!d) return "";
  try {
    return new Date(d).toLocaleTimeString("en-CA", { hour: "numeric", minute: "2-digit" });
  } catch { return ""; }
};

const DetailModal = ({ item, onClose, navigate }) => {
  if (!item) return null;
  const cfg = SVC[item.service_type] || SVC.cargo_delivery;
  const Icon = cfg.icon;
  const isOngoing = item.status === "accepted" && item.trip_status !== "completed";

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center"
      onClick={onClose} data-testid="activity-detail-overlay"
    >
      <motion.div
        initial={{ y: 100, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 100, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-t-3xl sm:rounded-3xl w-full sm:max-w-lg max-h-[85vh] overflow-y-auto shadow-2xl"
        data-testid="activity-detail-modal"
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-stone-100 p-4 flex items-center justify-between rounded-t-3xl z-10">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 ${cfg.color} rounded-xl flex items-center justify-center`}>
              <Icon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-emerald-950">{cfg.label}</h3>
              <div className="flex items-center gap-1.5">
                <StatusDot status={item.status} trip={item.trip_status} />
                <StatusText status={item.status} trip={item.trip_status} />
              </div>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center" data-testid="close-activity-detail">
            <X className="w-4 h-4 text-stone-600" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Trip Progress for ongoing */}
          {isOngoing && (
            <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4" data-testid="ongoing-banner">
              <div className="flex items-center gap-3">
                <Activity className="w-5 h-5 text-blue-600 animate-pulse" />
                <div>
                  <p className="font-semibold text-blue-800">
                    {item.trip_status === "en_route" ? "On the way to customer" :
                     item.trip_status === "arriving" ? "Almost there" :
                     item.trip_status === "arrived" ? "Waiting for customer" : "In progress"}
                  </p>
                  <p className="text-sm text-blue-600">Job is active</p>
                </div>
              </div>
            </div>
          )}

          {/* Customer */}
          <div className="bg-stone-50 rounded-2xl p-4" data-testid="customer-info">
            <p className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-2">Customer</p>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center overflow-hidden">
                {item.customer_picture ? (
                  <img src={item.customer_picture} alt="" className="w-10 h-10 rounded-full object-cover" />
                ) : (
                  <User className="w-5 h-5 text-emerald-600" />
                )}
              </div>
              <p className="font-semibold text-emerald-950">{item.customer_name}</p>
            </div>
          </div>

          {/* Location */}
          <div className="bg-stone-50 rounded-2xl p-4" data-testid="location-info">
            <p className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-2">Location</p>
            {item.pickup_address && (
              <div className="flex items-start gap-2 mb-2">
                <MapPin className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-stone-700">{item.pickup_address}</p>
              </div>
            )}
            {item.dropoff_address && (
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-stone-700">{item.dropoff_address}</p>
              </div>
            )}
          </div>

          {/* Timestamps */}
          <div className="bg-stone-50 rounded-2xl p-4" data-testid="time-info">
            <p className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-2">Timeline</p>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-stone-500">Requested</span>
                <span className="text-stone-800">{fmtDate(item.created_at)} {fmtTime(item.created_at)}</span>
              </div>
              {item.accepted_at && (
                <div className="flex justify-between">
                  <span className="text-stone-500">Accepted</span>
                  <span className="text-stone-800">{fmtDate(item.accepted_at)} {fmtTime(item.accepted_at)}</span>
                </div>
              )}
              {item.completed_at && (
                <div className="flex justify-between">
                  <span className="text-stone-500">Completed</span>
                  <span className="text-stone-800">{fmtDate(item.completed_at)} {fmtTime(item.completed_at)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Earnings */}
          {item.earned > 0 && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4" data-testid="earnings-info">
              <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wider mb-1">Earnings</p>
              <p className="text-2xl font-bold text-emerald-700">${item.earned.toFixed(2)}</p>
              {item.amount > 0 && (
                <p className="text-xs text-emerald-500 mt-0.5">Total charged: ${item.amount.toFixed(2)}</p>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="space-y-2" data-testid="activity-actions">
            {isOngoing && (
              <>
                <Button
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl"
                  onClick={() => { onClose(); navigate("/professional/dashboard"); }}
                  data-testid="go-to-dashboard-btn"
                >
                  <Navigation className="w-4 h-4 mr-2" /> Go to Dashboard
                </Button>
                <Button
                  variant="outline" className="w-full rounded-xl"
                  onClick={() => { onClose(); navigate(`/chat/${item.request_id}`); }}
                  data-testid="chat-customer-btn"
                >
                  <MessageCircle className="w-4 h-4 mr-2" /> Chat with Customer
                </Button>
              </>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

const ProActivityPage = () => {
  const [ongoing, setOngoing] = useState([]);
  const [past, setPast] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("ongoing");
  const [selected, setSelected] = useState(null);
  const navigate = useNavigate();

  useEffect(() => { fetchActivity(); }, []);

  const fetchActivity = async () => {
    try {
      const res = await axios.get(`${API}/professional/activity`, { withCredentials: true });
      setOngoing(res.data.ongoing || []);
      setPast(res.data.past || []);
    } catch (err) {
      console.error("Failed to fetch activity", err);
    } finally {
      setLoading(false);
    }
  };

  const items = tab === "ongoing" ? ongoing : past;

  return (
    <div className="min-h-screen bg-stone-50 pb-20" data-testid="pro-activity-page">
      {/* Header */}
      <section className="relative pt-14 pb-5 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-950 via-emerald-900 to-emerald-950" />
        <div className="max-w-5xl mx-auto px-4 relative z-10">
          <Link to="/professional/dashboard" className="inline-flex items-center gap-2 text-emerald-300 hover:text-white mb-3 transition-colors text-sm">
            <ArrowLeft className="w-4 h-4" /> Dashboard
          </Link>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-lime-400 rounded-2xl flex items-center justify-center">
              <Activity className="w-6 h-6 text-emerald-950" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Activity</h1>
              <p className="text-emerald-300 text-sm">{ongoing.length} ongoing &middot; {past.length} past</p>
            </div>
          </div>
        </div>
      </section>

      {/* Tabs */}
      <div className="sticky top-0 z-30 bg-white border-b border-stone-200 shadow-sm">
        <div className="max-w-5xl mx-auto px-4 py-3 flex gap-2">
          {[
            { key: "ongoing", label: "Ongoing", count: ongoing.length },
            { key: "past", label: "Past", count: past.length },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                tab === t.key ? "bg-emerald-950 text-white" : "bg-stone-100 text-stone-600 hover:bg-stone-200"
              }`}
              data-testid={`tab-${t.key}`}
            >
              {t.label}
              <span className="ml-1.5 text-xs opacity-70">{t.count}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <section className="py-5 px-4">
        <div className="max-w-5xl mx-auto">
          {loading ? (
            <div className="flex items-center justify-center py-20 gap-2">
              <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
              <span className="text-stone-500">Loading activity...</span>
            </div>
          ) : items.length === 0 ? (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-20" data-testid="empty-activity">
              <Activity className="w-12 h-12 text-stone-300 mx-auto mb-3" />
              <p className="text-stone-500 font-medium">
                {tab === "ongoing" ? "No ongoing jobs" : "No past activity yet"}
              </p>
              <p className="text-sm text-stone-400 mt-1">
                {tab === "ongoing" ? "Accept a job to see it here." : "Completed and declined jobs will appear here."}
              </p>
            </motion.div>
          ) : (
            <div className="space-y-3" data-testid="activity-list">
              <AnimatePresence mode="popLayout">
                {items.map((item, i) => {
                  const cfg = SVC[item.service_type] || SVC.cargo_delivery;
                  const Icon = cfg.icon;
                  return (
                    <motion.div
                      key={item.request_id}
                      layout
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      transition={{ delay: i * 0.02 }}
                      onClick={() => setSelected(item)}
                      className="bg-white rounded-2xl border border-stone-100 p-4 cursor-pointer hover:border-emerald-200 hover:shadow-sm transition-all"
                      data-testid={`activity-card-${item.request_id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-11 h-11 ${cfg.color} rounded-xl flex items-center justify-center flex-shrink-0`}>
                          <Icon className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <span className="font-semibold text-emerald-950 text-sm">{cfg.label}</span>
                            <div className="flex items-center gap-1.5">
                              <StatusDot status={item.status} trip={item.trip_status} />
                              <StatusText status={item.status} trip={item.trip_status} />
                            </div>
                          </div>
                          <div className="flex items-center gap-3 text-xs text-stone-500 mb-1">
                            <span className="flex items-center gap-1">
                              <User className="w-3 h-3" />
                              {item.customer_name}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {fmtDate(item.accepted_at || item.created_at)}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            {item.pickup_address && (
                              <span className="text-xs text-stone-400 truncate flex items-center gap-1 max-w-[65%]">
                                <MapPin className="w-3 h-3 flex-shrink-0" />
                                {item.pickup_address.substring(0, 40)}{item.pickup_address.length > 40 ? "..." : ""}
                              </span>
                            )}
                            {item.earned > 0 && (
                              <span className="text-sm font-bold text-emerald-700 flex-shrink-0" data-testid={`earned-${item.request_id}`}>
                                +${item.earned.toFixed(2)}
                              </span>
                            )}
                          </div>
                        </div>
                        <ChevronRight className="w-4 h-4 text-stone-300 flex-shrink-0" />
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          )}
        </div>
      </section>

      {/* Detail Modal */}
      <AnimatePresence>
        {selected && (
          <DetailModal
            item={selected}
            onClose={() => setSelected(null)}
            navigate={navigate}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default ProActivityPage;
